import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { Home, User, BookOpen, LogOut } from 'lucide-react'
import { GlobalFooter } from '../../components/GlobalFooter'
import { useDeviceCheck } from "../../hooks/useDeviceCheck"

export function ClientLayout({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate()
  const location = useLocation()
  const [mobileOpen, setMobileOpen] = useState(false)

  const [profile, setProfile] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  // 🔹 Load profile
  useEffect(() => {
    async function loadProfile() {
      try {
        const { data: authData } = await supabase.auth.getUser()

        if (!authData?.user) {
          navigate('/login')
          return
        }

        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', authData.user.id)
          .single()

        if (error) {
          console.error(error)
          return
        }

        setProfile(data)
      } catch (err) {
        console.error('Profile load error:', err)
      } finally {
        setLoading(false)
      }
    }

    loadProfile()
  }, [navigate])

  useDeviceCheck(profile ? { id: profile.id } : null);

  // 🔹 Redirect logic (always defined — no conditional hook)
  useEffect(() => {
    if (!profile) return

    const isProfilePage = location.pathname.startsWith('/dashboard/profile')
const isSuspendedPage = location.pathname.startsWith('/dashboard/suspended')

const isPending = profile.status === 'pending'
const isSuspended = profile.status === 'suspended'

// Pending users → only allow profile
if (isPending && !isProfilePage) {
  navigate('/dashboard/profile')
  return
}

// Suspended users → allow profile + suspended page only
if (isSuspended && !isProfilePage && !isSuspendedPage) {
  navigate('/dashboard/suspended')
  return
}
  }, [profile, location.pathname, navigate])

  const handleLogout = async () => {
    await supabase.auth.signOut()
    navigate('/login')
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        Loading...
      </div>
    )
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        Failed to load profile
      </div>
    )
  }

  const isProfilePage = location.pathname.startsWith('/dashboard/profile')

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <header className="bg-nhs-blue text-white">
        {mobileOpen && (
  <div className="md:hidden bg-white shadow-md border-t">

    <div className="px-4 py-4 space-y-3">

      <Link
        to="/dashboard"
        onClick={() => setMobileOpen(false)}
        className="block bg-white border border-gray-200 rounded-lg px-4 py-3 text-nhs-blue font-bold hover:bg-gray-50 transition"
      >
        Community
      </Link>

      <Link
        to="/dashboard/profile"
        onClick={() => setMobileOpen(false)}
        className="block bg-white border border-gray-200 rounded-lg px-4 py-3 text-nhs-blue font-bold hover:bg-gray-50 transition"
      >
        My Profile
      </Link>

      <Link
        to="/dashboard/training"
        onClick={() => setMobileOpen(false)}
        className="block bg-white border border-gray-200 rounded-lg px-4 py-3 text-nhs-blue font-bold hover:bg-gray-50 transition"
      >
        Training Sessions
      </Link>

      <button
        onClick={handleLogout}
        className="block w-full text-left bg-white border border-gray-200 rounded-lg px-4 py-3 text-red-600 font-bold hover:bg-red-50 transition"
      >
        Sign Out
      </button>

    </div>

  </div>
)}
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between h-16">
          <Link to="/dashboard" className="font-bold text-lg">
            Talis'on Services
          </Link>

          <div className="flex items-center gap-4">
            <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden text-white"
          >
           ☰
          </button>
            <nav className="hidden md:flex items-center gap-3">

  <Link
    to="/dashboard"
    className={`px-4 py-2 rounded-lg font-bold transition ${
      location.pathname === "/dashboard"
        ? "bg-nhs-pale text-nhs-blue"
        : "bg-white text-nhs-blue hover:bg-gray-100"
    }`}
  >
    Community
  </Link>

  <Link
    to="/dashboard/profile"
    className={`px-4 py-2 rounded-lg font-bold transition ${
      location.pathname.startsWith("/dashboard/profile")
        ? "bg-nhs-pale text-nhs-blue"
        : "bg-white text-nhs-blue hover:bg-gray-100"
    }`}
  >
    My Profile
  </Link>

  <Link
    to="/dashboard/training"
    className={`px-4 py-2 rounded-lg font-bold transition ${
      location.pathname.startsWith("/dashboard/training")
        ? "bg-nhs-pale text-nhs-blue"
        : "bg-white text-nhs-blue hover:bg-gray-100"
    }`}
  >
    Training Sessions
  </Link>

</nav>

            <button
              onClick={handleLogout}
              className="flex items-center gap-1 text-sm"
            >
              <LogOut size={16} />

              Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl mx-auto px-4 py-6 w-full">
        {profile.status === 'pending' && (
          <div className="mb-6 bg-gray-500 border border-nhs-blue p-4 rounded">
            <h2 className="text-white font-semibold">
              Your account is awaiting admin approval. Kindly proceed to complete your account profile information.
            </h2>
            <p className= "text-white font-medium">
              1. Pending approval, click on the google form link that was sent to your email, take your time to fill this correctly then submit.
            </p>
            <p className= "text-white font-medium">
              2. Download the submitted form and upload below along with your CV.
            </p>
          </div>
          
        )}

        {profile.status === 'suspended' && (
          <div className="mb-6 bg-red-50 border border-red-200 p-4 rounded">
            <p className="text-red-800 font-medium">
              Your account has been suspended.
            </p>
          </div>
        )}

        {profile.status === 'active' || isProfilePage ? children : null}
      </main>

      <GlobalFooter />
    </div>
  )
}